import { ContractContact, ContactType, ContractPhase, RentalContract, MilestoneDateType, ContractMilestoneDate } from "shared/types/rental-contracts";

import { Flag, ListBullets, Path, ProhibitInset, StackSimple, UserCircle } from "@phosphor-icons/react";
import { useState } from "react";
import { useEffect } from "react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { Checkbox, Chip, Popover, PopoverContent, PopoverTrigger, Table, TableHeader, TableBody, TableRow, TableColumn, TableCell, Tooltip, Spinner } from "@heroui/react";
import { ArrowUpRight, Building, ChevronDown, Close, Link } from "assets/icons";
import SearchComponent from "shared/components/searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";
import { SearchMode } from "shared/components/searchBar/components/searchComponent";
import { applyMask } from "shared/utils/masks";

export default function AllContracts() {
    const [rentalContracts, setRentalContracts] = useState<RentalContract[]>([]);
    const { session } = useSession();
    const [isLoadingRentalContracts, setIsLoadingRentalContracts] = useState<boolean>(true);

    const [selectedPhases, setSelectedPhases] = useState<ContractPhase[]>([]);
    const [selectedResource, setSelectedResource] = useState<{
        id: string;
        type: ResourceType;
    } | null>(null);

    const fetchAllRentalContracts = async () => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/rental/contracts/active`,
                { method: "GET" },
                session,
            );
            const data = await response.json();
            console.log("Fetched data:", data); // Log the raw data

            // Ensure the data is an array
            if (Array.isArray(data)) {
                setRentalContracts(data);
            } else {
                console.error("Unexpected data format:", data);
                setRentalContracts([]); // Ensure rentalContracts is always an array
            }
        } catch (error) {
            console.error("Failed to fetch rental contracts:", error);
            setRentalContracts([]); // Ensure rentalContracts is always an array
        } finally {
            setIsLoadingRentalContracts(false);
        }
    };

    useEffect(() => {
        console.log(rentalContracts);
        fetchAllRentalContracts();
    }, []);

    const getStartDate = (contract: RentalContract) => {
        const milestoneDates =
            contract &&
            contract?.contractMilestoneDates?.filter(
                (date: ContractMilestoneDate) =>
                    date.milestoneDateType == MilestoneDateType.START_DATE,
            );
        if (milestoneDates?.length) {
            return milestoneDates[0].date?.toString();
        } else {
            return "Geen startdatum gevonden";
        }
    };

    const handlePhaseChange = (phase: ContractPhase) => {
        setSelectedPhases((prevSelected) =>
            prevSelected.includes(phase)
                ? prevSelected.filter((item) => item !== phase)
                : [...prevSelected, phase],
        );
    };
    const filteredContracts = rentalContracts.filter((contract) => {
        const phaseMatch =
            selectedPhases.length === 0 || selectedPhases.includes(contract.phase);
        const resourceMatch =
            !selectedResource ||
            // (selectedResource.type === ResourceType.BUILDING &&
            //   contract.buildingId === selectedResource.id) ||
            (selectedResource.type === ResourceType.UNIT &&
                contract.unitId === selectedResource.id);

        return phaseMatch && resourceMatch;
    });

    const handleSelectBuildingOrUnit = (resource: {
        id: string;
        type: ResourceType;
    }) => {
        setSelectedResource(resource);
    };


    return (

        <div id="all-contracts" className="col-span-full flex flex-col gap-2">
            <header className="flex items-center gap-1.5 text-lg font-medium">
                <ListBullets weight="duotone" size={20} />
                Alle contracten
            </header>
            <div className="flex flex-col divide-y divide-neutral-primary/10 rounded-lg bg-white shadow-sm">
                <div className="sticky top-20 z-20 flex items-center justify-between rounded-tl-lg rounded-tr-lg bg-white/80 p-2.5 shadow-sm backdrop-blur-md">
                    <b className="text-sm">Filters:</b>
                    <div className="5 flex gap-2">
                        <div className="flex items-center rounded-full border border-neutral-primary/20 px-2.5 py-1 text-sm font-medium">
                            <Popover placement="bottom" offset={10}>
                                <PopoverTrigger className="focus:outline-hidden">
                                    <a className="flex cursor-pointer items-center gap-1 text-moss-primary">
                                        <Path size={18} /> Fase
                                        <ChevronDown className="h-[18px] w-[18px]" />
                                    </a>
                                </PopoverTrigger>
                                <PopoverContent>
                                    <ul className="flex flex-col gap-2 px-1 py-2">
                                        {Object.values(ContractPhase).map((phase) => (
                                            <li>
                                                <Checkbox
                                                    classNames={{ label: "text-sm" }}
                                                    checked={selectedPhases.includes(phase)}
                                                    onChange={() => handlePhaseChange(phase)}
                                                >
                                                    {phase}
                                                </Checkbox>
                                            </li>
                                        ))}
                                    </ul>
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className="w-[250px]">
                            <SearchComponent
                                label="Zoek gebouw of unit"
                                onSelect={(resource: any) =>
                                    handleSelectBuildingOrUnit(resource)
                                }
                                mode={SearchMode.SEARCH_AND_PRESENT}
                                editMode={true}
                                resourceFilter={[
                                    //ResourceType.BUILDING,
                                    ResourceType.UNIT,
                                ]}
                            />
                        </div>
                    </div>
                    {selectedResource && (
                        <div className="flex items-center gap-3 rounded-full border border-neutral-primary/20 p-2 px-3 text-sm">
                            <p className="font-medium">Filters:</p>
                            <Chip
                                className="flex items-center gap-1"
                                startContent={
                                    <Building className="h-3.5 w-3.5 text-neutral-primary" />
                                }
                                endContent={
                                    <button
                                        onClick={() => setSelectedResource(null)}
                                        aria-label="Remove building or unit"
                                    >
                                        <Close className="h-3.5 w-3.5 text-status-bad" />
                                    </button>
                                }
                                classNames={{
                                    base: "h-fit bg-white px-2 border bg-transparent border-neutral-primary text-neutral-primary",
                                    content: "px-0 font-medium",
                                }}
                            >
                                {selectedResource.type === ResourceType.BUILDING
                                    ? `Gebouw ID: ${selectedResource.id}`
                                    : `Unit ID: ${selectedResource.id}`}
                            </Chip>
                        </div>
                    )}
                </div>
                {isLoadingRentalContracts ? (
                    <div className="flex h-full items-center justify-center">
                        <Spinner
                            label="Gegevens aan het ophalen"
                            color="success"
                            size="md"
                        />
                    </div>
                ) : (
                    <Table
                        aria-label="Example static collection table"
                        classNames={{
                            wrapper: "shadow-none rounded-lg p-0",
                            thead: "h-fit border-b-2 border-neutral-primary/40",
                            th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
                            tbody: "border-b divide-y divide-neutral-primary/15",
                            tr: "divide-x divide-neutral-primary/15",
                            td: "py-2.5",
                        }}
                    >
                        <TableHeader>
                            <TableColumn>Unit</TableColumn>
                            <TableColumn>Oppervlakte</TableColumn>
                            <TableColumn>Type</TableColumn>
                            <TableColumn>Startdatum</TableColumn>
                            <TableColumn>Huurder</TableColumn>
                            <TableColumn>Geïndexeerde huurprijs</TableColumn>
                            <TableColumn>Fase</TableColumn>
                            <TableColumn>Acties</TableColumn>
                        </TableHeader>
                        <TableBody>
                            {filteredContracts &&
                                filteredContracts.map((contract: RentalContract) => (
                                    <TableRow key={contract.id}>
                                        <TableCell>
                                            <Chip
                                                className="gap-1"
                                                startContent={
                                                    <StackSimple weight="duotone" size={14} />
                                                }
                                                classNames={{
                                                    base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                                                    content: "px-0 font-medium",
                                                }}
                                            >
                                                {contract.unit?.id}
                                            </Chip>
                                        </TableCell>
                                        <TableCell>{contract.unit?.grossSurface || contract.unit?.netSurface || '-'} m²</TableCell>
                                        <TableCell>{contract.unit?.unitType || '-'}</TableCell>
                                        <TableCell>{getStartDate(contract)}</TableCell>
                                        <TableCell>

                                            {contract.contractContacts &&
                                                contract.contractContacts
                                                    .filter(
                                                        (contact: ContractContact) =>
                                                            contact.contactType == ContactType.TENANT,
                                                    )
                                                    .map((contact: any) => (
                                                        <div
                                                            key={contact.id}
                                                            className="flex items-center"
                                                        >
                                                            <Chip
                                                                className="gap-1"
                                                                startContent={<UserCircle weight="duotone" size={14} />}
                                                                classNames={{
                                                                    base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                                                                    content: "px-0 font-medium",
                                                                }}
                                                            >
                                                                <Link
                                                                    to={`/contact/${contact.resource.resourceId}`}
                                                                    className="text-moss-primary"
                                                                >
                                                                    {contact.resource?.name}
                                                                </Link>
                                                                {contact.resource?.name}
                                                            </Chip>
                                                            <ArrowUpRight className="ml-1 h-4 w-4" />
                                                        </div>
                                                    ))}
                                        </TableCell>

                                        <TableCell>
                                            <b>
                                                {applyMask(contract.activePrice?.price, "valuta")}
                                            </b>
                                        </TableCell>
                                        <TableCell>{contract.phase}</TableCell>
                                        <TableCell>
                                            <div className="flex cursor-pointer items-center gap-3">
                                                <Tooltip content="Van watchlist verwijderen">
                                                    <Flag weight="duotone" size={22} />
                                                </Tooltip>
                                                <Tooltip content="Contract opzeggen">
                                                    <a
                                                        className="text-status-bad"
                                                    // onClick={() =>
                                                    //   handleTerminateContract(contract)
                                                    // }
                                                    >
                                                        <ProhibitInset weight="duotone" size={22} />
                                                    </a>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                )}
            </div>
        </div>

    )
}